import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Paper,
} from '@mui/material';
import { config } from '../config';

function TwoFactorSetup() {
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  const handleSetup2FA = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.endpoints.auth}/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to setup 2FA');
      }

      const data = await response.json();
      setSetupData(data);
      setShowSetupDialog(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.endpoints.auth}/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: verificationCode }),
      });

      if (!response.ok) {
        throw new Error('Invalid verification code');
      }

      setIs2FAEnabled(true);
      setShowSetupDialog(false);
      setVerificationCode('');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.endpoints.auth}/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disable 2FA');
      }

      setIs2FAEnabled(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Two-Factor Authentication
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box>
        {is2FAEnabled ? (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDisable2FA}
            disabled={loading}
          >
            {loading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSetup2FA}
            disabled={loading}
          >
            {loading ? 'Setting up...' : 'Setup 2FA'}
          </Button>
        )}
      </Box>

      <Dialog open={showSetupDialog} onClose={() => !loading && setShowSetupDialog(false)}>
        <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            1. Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </Typography>
          
          {setupData?.qrCode && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img src={setupData.qrCode} alt="2FA QR Code" />
            </Box>
          )}

          <Typography variant="body1" paragraph>
            2. Enter the verification code from your authenticator app:
          </Typography>

          <TextField
            autoFocus
            margin="dense"
            label="Verification Code"
            type="text"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSetupDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleVerify2FA}
            disabled={loading || !verificationCode}
            variant="contained"
            color="primary"
          >
            {loading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default TwoFactorSetup;
